import { useEffect, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Wallet, CheckCircle2, Upload, Loader2 } from "lucide-react";

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
};

export default function SuperWallet() {
  const [data, setData] = useState(null);
  const [percent, setPercent] = useState("10");
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [forms, setForms] = useState({});
  const [hydrated, setHydrated] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get("/platform/wallet");
      setData(d);
      if (!hydrated) {
        setPercent(String(d.referral_commission_percent ?? 10));
        setHydrated(true);
      }
    } catch (e) {
      toast.error(extractErr(e));
    }
  };
  useEffect(() => { load(); }, []);

  const savePercent = async (e) => {
    e.preventDefault();
    const n = Number(percent);
    if (!Number.isFinite(n) || n < 0 || n > 100) {
      toast.error("Enter a percent between 0 and 100");
      return;
    }
    setSaving(true);
    try {
      const { data: res } = await api.put("/platform/wallet/settings", { referral_commission_percent: n });
      setPercent(String(res.referral_commission_percent ?? n));
      toast.success("Commission percent saved");
      load();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setSaving(false);
    }
  };

  const setForm = (id, patch) => setForms((f) => ({ ...f, [id]: { ...(f[id] || {}), ...patch } }));

  const uploadProof = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: res } = await api.post("/platform/wallet/upload", fd);
      setForm(id, { proof_url: res.url });
      toast.success("Screenshot uploaded");
    } catch (e) {
      toast.error(extractErr(e));
    }
  };

  const markDone = async (row) => {
    const f = forms[row.id] || {};
    setBusyId(row.id);
    try {
      await api.post(`/platform/wallet/withdrawals/${row.id}/complete`, {
        txn_id: f.txn_id || "",
        proof_url: f.proof_url || null,
      });
      toast.success("Marked done — merchant emailed");
      load();
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="space-y-8 max-w-5xl" data-testid="super-wallet-page">
      <div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Wallet className="w-7 h-7 text-indigo-300" /> Wallet & Referal</h1>
        <p className="text-slate-500 text-sm mt-1">Set the merchant referral allowance and complete wallet withdrawals manually.</p>
      </div>

      <Card className="bg-slate-900 border-slate-800 p-5 text-slate-100">
        <form onSubmit={savePercent} className="flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="flex-1 max-w-xs">
            <Label htmlFor="referral-percent" className="text-slate-300">Merchant earning allowance (%)</Label>
            <div className="mt-1 flex items-stretch rounded-lg border border-slate-600 bg-slate-950 overflow-hidden focus-within:ring-2 focus-within:ring-indigo-400">
              <input
                id="referral-percent"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={percent}
                onChange={(e) => setPercent(e.target.value.replace(/[^\d.]/g, ""))}
                className="flex-1 min-w-0 bg-transparent px-3 py-2 text-lg text-white caret-white outline-none font-mono-nums"
                data-testid="referral-percent"
                placeholder="10"
              />
              <span className="px-3 py-2 text-slate-400 text-sm font-semibold border-l border-slate-700 bg-slate-900 select-none">%</span>
            </div>
            <p className="text-xs text-slate-500 mt-2">Applied to the list price excluding GST. Coupons do not reduce this base. Example: 10% of ₹1999 = ₹199.90 even with 100% off.</p>
          </div>
          <Button type="submit" disabled={saving} data-testid="save-referral-percent">{saving ? "Saving…" : "Save percent"}</Button>
        </form>
      </Card>

      <div>
        <h2 className="font-semibold text-lg mb-3">Pending withdrawals</h2>
        {!data ? (
          <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (data.pending || []).length === 0 ? (
          <p className="text-slate-500 text-sm">No pending requests.</p>
        ) : (
          <div className="space-y-4">
            {data.pending.map((row) => {
              const f = forms[row.id] || {};
              const p = row.payout || {};
              return (
                <Card key={row.id} className="bg-slate-900 border-slate-800 p-5 space-y-3" data-testid={`wd-${row.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold">{row.store_name} <span className="text-slate-500 font-normal">/{row.store_slug}</span></div>
                      <div className="text-xs text-slate-500">{row.merchant_email} · {fmt(row.requested_at)}</div>
                    </div>
                    <div className="text-xl font-bold font-mono-nums text-amber-300">{INR(row.amount)}</div>
                  </div>
                  <div className="text-xs text-slate-400 space-y-0.5">
                    {p.upi_id && <div>UPI: <span className="text-slate-200 font-mono">{p.upi_id}</span></div>}
                    {(p.account_number || p.ifsc) && (
                      <div>Bank: {p.account_holder} · {p.account_number} · {p.ifsc} · {p.bank_name}</div>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-400">Transaction ID</Label>
                      <Input className="bg-slate-950 border-slate-700 mt-1" value={f.txn_id || ""} onChange={(e) => setForm(row.id, { txn_id: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-slate-400">Screenshot / proof</Label>
                      <div className="mt-1 flex items-center gap-2">
                        <label className="inline-flex items-center gap-2 text-sm text-indigo-300 cursor-pointer">
                          <Upload className="w-4 h-4" /> Upload
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadProof(row.id, e.target.files?.[0])} />
                        </label>
                        {f.proof_url && <a href={f.proof_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-400 truncate">View</a>}
                      </div>
                    </div>
                  </div>
                  <Button size="sm" onClick={() => markDone(row)} disabled={busyId === row.id} data-testid={`wd-done-${row.id}`}>
                    {busyId === row.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    Mark as done
                  </Button>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {(data?.history || []).length > 0 && (
        <div>
          <h2 className="font-semibold text-lg mb-3">Completed</h2>
          <div className="rounded-xl border border-slate-800 overflow-hidden">
            {data.history.map((row) => (
              <div key={row.id} className="px-4 py-3 border-b border-slate-800 last:border-0 flex justify-between gap-3 text-sm">
                <div>
                  <div className="text-slate-200">{row.store_name}</div>
                  <div className="text-xs text-slate-500">{fmt(row.processed_at)} {row.txn_id ? `· ${row.txn_id}` : ""}</div>
                </div>
                <div className="text-emerald-300 font-mono-nums">{INR(row.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
